import { useState, useEffect } from "react";
import { BookUser, MapPin, Loader2, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { resolveAddress } from "@/components/AddressesSection";
import { isSenderAddress, isRecipientAddress } from "@/lib/addressPrefs";

// Botón-ícono de libreta de direcciones que abre un modal con todas las
// direcciones guardadas del usuario (perfil + libreta) para autollenar
// el campo donde se coloque (origen/remitente o destino/destinatario).
// onPick recibe { name, phone, street, number, colony, city, state, cp, country, references, email, label }.
export default function AddressBookButton({ onPick, mode, title = "Libreta de direcciones", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const all = [];
      // 1) Direcciones del perfil (Domicilio, Fiscal, Facturación, Envíos).
      //    Se filtran por modo: remitente (origen) muestra Domicilio/Fiscal/Facturación;
      //    destinatario (destino) muestra Envíos. Sin modo (undefined) muestra todas.
      if (user) {
        const perfilEntries = user.addr_same_all
          ? [["home", "Domicilio", "remitente"]]
          : [["home", "Domicilio", "remitente"], ["fiscal", "Fiscal", "remitente"], ["billing", "Facturación", "remitente"], ["shipping", "Envíos", "destinatario"]];
        perfilEntries.forEach(([key, label, type]) => {
          if (mode === "sender" && type !== "remitente") return;
          if (mode === "recipient" && type !== "destinatario") return;
          const a = resolveAddress(user, key);
          if (a?.cp) all.push({ ...a, label, type, source: "perfil", id: `perfil-${key}` });
        });
      }
      // 2) Libreta de direcciones (entity Address). Se filtra por modo usando
      //    las flags as_sender / as_recipient / as_shipping y el campo type;
      //    si ninguno coincide, se muestran todas las de ese usuario.
      try {
        // En cotizadores/formularios, cada usuario (incluido el admin) solo ve
        // SUS propias direcciones guardadas. La vista global de todas las
        // libretas vive exclusivamente en la pestaña de administración.
        const recs = await base44.entities.Address.filter({ created_by_id: user.id }, { sort: "-updated_date", limit: 100 });
        let items = recs?.items || [];
        if (mode === "sender") {
          items = items.filter(isSenderAddress);
        } else if (mode === "recipient") {
          items = items.filter(isRecipientAddress);
        }
        items.forEach(r => all.push({ ...r, source: "libreta", id: r.id }));
      } catch { /* sin libreta */ }
      if (!cancelled) { setItems(all); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  // Solo mostrar el botón de libreta a usuarios con sesión iniciada.
  // En cotizadores/formularios públicos (sin login) no se renderiza.
  if (!user) return null;

  const pick = (a) => { onPick?.(a); setOpen(false); };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={title} aria-label={title}
        className={`inline-flex items-center justify-center text-cyan hover:text-cyan-hover transition-colors ${className}`}>
        <BookUser className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><BookUser className="w-4 h-4 text-cyan" /> Libreta de direcciones</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-cyan" /></div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-muted leading-relaxed">
              No tienes direcciones guardadas.<br />Agrégalas en <b className="text-ink-text">Mi Perfil → Mis Direcciones</b> o en <b className="text-ink-text">Libreta de Direcciones</b>.
            </div>
          ) : (
            <div className="grid gap-2 max-h-[60vh] overflow-y-auto pr-1">
              {items.map(a => (
                <button key={a.id} type="button" onClick={() => pick(a)}
                  className="surface-inset p-3 text-left hover:border-cyan transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-3.5 h-3.5 text-cyan shrink-0" />
                    <span className="font-display font-bold text-sm">{a.label || a.name || "Dirección"}</span>
                    {a.is_default && <Star className="w-3 h-3 text-cyan fill-cyan" />}
                    <div className="ml-auto flex flex-wrap gap-1 justify-end">
                      <span className="text-[0.6rem] uppercase tracking-wider text-ink-muted px-1.5 py-0.5 border border-ink-border">
                        {a.source === "perfil" ? "Perfil" : a.type}
                      </span>
                      {a.as_sender && <span className="text-[0.6rem] uppercase tracking-wider text-cyan px-1.5 py-0.5 border border-cyan">Remitente</span>}
                      {a.as_recipient && <span className="text-[0.6rem] uppercase tracking-wider text-cyan px-1.5 py-0.5 border border-cyan">Destinatario</span>}
                      {a.as_shipping && <span className="text-[0.6rem] uppercase tracking-wider text-cyan px-1.5 py-0.5 border border-cyan">Envío</span>}
                    </div>
                  </div>
                  <div className="text-xs text-ink-muted leading-relaxed">
                    {a.name && a.name !== a.label && <div className="text-ink-text font-medium">{a.name}</div>}
                    {[a.street, a.number, a.colony, a.city, a.state].filter(Boolean).join(", ")}
                    {a.cp ? ` · C.P. ${a.cp}` : ""}
                    {a.phone ? ` · ${a.phone}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}